import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { tradeStore } from '@/models/stores/trades';
import type { Trade, TradeStatus } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { ProgressRing } from '../../ui/progress-ring/progress-ring';
import { TradeStatusBadge } from './trade-status-badge';

export const getStatusOptions = ({
  className,
}: {
  className: string;
}): Option<TradeStatus>[] => {
  return [
    {
      value: 'evaluation',
      label: 'Evaluation',
      icon: (
        <ProgressRing
          percentage={30}
          className={className}
          circleClassName="text-blue-500"
        />
      ),
      hotkey: '1',
    },
    {
      value: 'pending',
      label: 'Pending',
      icon: (
        <ProgressRing
          percentage={40}
          className={className}
          circleClassName="text-blue-500"
        />
      ),
      hotkey: '2',
    },
    {
      value: 'accepted',
      label: 'Accepted',
      icon: (
        <ProgressRing
          percentage={50}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '3',
    },
    {
      value: 'inbound',
      label: 'Inbound',
      icon: (
        <ProgressRing
          percentage={60}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '4',
    },
    {
      value: 'received',
      label: 'Received',
      icon: (
        <ProgressRing
          percentage={70}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '5',
    },
    {
      value: 'completed',
      label: 'Completed',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '6',
    },
    {
      value: 'rejected',
      label: 'Rejected',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-red-500"
        />
      ),
      hotkey: '7',
    },
  ];
};

export const STATUS_OPTIONS_MAP = getStatusOptions({ className: 'size-4' });

export type TradeStatusSelectorProps = {
  id: Trade['id'];
  value: Trade['status'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function TradeStatusSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  // DEV UX NOTE: do not turn `withHotkeyListener` in any list or board views
  // because it will lag the page. This is purely meant for limited number of
  // DealStatusSelectors on a page.
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: TradeStatusSelectorProps) {
  const statusButtonRef = useRef<HTMLButtonElement>(null);

  // /api/trades/:trade_id/status
  const updateStatusMutation = useMutation({
    onMutate: async ({ status }) => {
      tradeStore.update({ id, trade: { status } });
    },
    mutationFn: async ({ status }: { status: TradeStatus }) => {
      const response = await pdkAxios
        .post(`/trades/${id}/status`, { status })
        .catch((e) => {
          console.error('Error updating status', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating status', error, variables);
    },
    onSettled: () => {},
  });

  const handleStatusChange = (newStatus: TradeStatus) => {
    updateStatusMutation.mutate({ status: newStatus });
  };

  return (
    <div>
      <ComboboxPopover
        endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_STATUS}</KeyboardKeyIcon>}
        placeholder="Set status..."
        onChange={handleStatusChange}
        emptyMessage="No status found"
        options={STATUS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = STATUS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleStatusChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div
        // DEV NOTE: without this div, combobox-popover won't work
        // that is, clicks will not open the combobox
        // I think the reason is children node needs to be able to receive a ref
        // in order to detect a click event, which makes it open
        // thus so this div is a workaround
        >
          <TradeStatusBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.DEAL_STATUS}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
